import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, BarChart3, Target, Settings, ExternalLink, Shield } from 'lucide-react';
import { TOP_BAR } from '../mock/mock';

function Section({ num, icon: Icon, title, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-4 mb-5">
        <div className="flex items-center gap-3">
          <span className="font-serif-display text-[#2BA8C0] text-3xl md:text-4xl leading-none shrink-0">{num}</span>
          {Icon && (
            <span className="w-10 h-10 rounded-xl bg-[#2BA8C0]/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#2BA8C0]" />
            </span>
          )}
        </div>
        <h2 className="font-serif-display text-[#1a1a1a] text-2xl md:text-3xl leading-tight">{title}</h2>
      </div>
      <div className="text-[#3a3a3a] text-[15px] leading-relaxed space-y-3 pl-0 md:pl-12">{children}</div>
    </section>
  );
}

export default function CookiePolicy() {
  const browsers = [
    { name: 'Google Chrome', url: 'https://support.google.com/chrome/answer/95647' },
    { name: 'Mozilla Firefox', url: 'https://support.mozilla.org/hu/kb/sutik-engedelyezese-es-tiltasa' },
    { name: 'Microsoft Edge', url: 'https://support.microsoft.com/en-us/microsoft-edge/view-cookies-in-microsoft-edge-a7d95376-f2cd-8e4a-25dc-1de753474879' },
    { name: 'Safari (Mac)', url: 'https://support.apple.com/hu-hu/guide/safari/sfri11471/mac' },
  ];

  const adServices = [
    { name: 'Google Ads', url: 'https://www.google.com/intl/hu/policies/privacy' },
    { name: 'Facebook (Meta)', url: 'https://www.facebook.com/help/cookies/' },
    { name: 'Instagram', url: 'https://help.instagram.com/402411646841720' },
    { name: 'TikTok Pixel', url: 'https://www.tiktok.com/legal/page/us/privacy-policy/en' },
    { name: 'Google Analytics', url: 'https://www.google.com/intl/hu/policies/privacy' },
    { name: 'Google Tag Manager', url: 'https://www.google.com/intl/hu/policies/privacy' },
    { name: 'Microsoft Clarity', url: 'https://clarity.microsoft.com/privacy' },
    { name: 'GoHighLevel', url: 'https://www.gohighlevel.com/privacy-policy' },
    { name: 'ActiveCampaign', url: 'https://www.activecampaign.com/legal/privacy-policy' },
  ];

  return (
    <div className="min-h-screen bg-[#f5f0e8]">
      {/* Top hero */}
      <div className="relative bg-gradient-to-b from-[#0e2030] via-[#0d2336] to-[#0a1d2e] text-white pt-12 pb-32 overflow-hidden">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#2BA8C0] text-[14px] mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-px bg-[#2BA8C0]" />
            <span className="text-[12px] uppercase tracking-[0.3em] text-[#2BA8C0] font-medium">Süti szabályzat</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] mb-5">
            Cookie (süti)<br /><span className="italic text-[#2BA8C0]">szabályzat</span>
          </h1>
          <p className="text-white/65 text-[15px]">Hatálybalépés dátuma: 2026. április 16.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-20 relative z-10 pb-24">
        <article className="bg-white rounded-3xl shadow-xl border border-[#e8e4dc] p-8 md:p-14">

          {/* Intro */}
          <div className="mb-12 pb-10 border-b border-[#e8e4dc]">
            <p className="text-[#3a3a3a] text-[15px] leading-relaxed mb-4">
              Jelen szabályzat az <strong>Eco-Lak Építő Kft.</strong> weboldalán alkalmazott sütikre
              vonatkozik. A weboldal használatával Ön elfogadja a jelen szabályzat rendelkezéseit.
              Amennyiben nem ért egyet a feltételekkel, kérjük, ne használja a weboldalt.
            </p>
          </div>

          <Section num="01" icon={Cookie} title="Mik azok a sütik (cookie-k)?">
            <p>
              A cookie-k kisméretű adatfájlok, amelyeket a weboldal küld a böngészőnek. Segítik a weboldal
              működését, javítják a felhasználói élményt, és lehetővé teszik a felhasználói viselkedés
              elemzését, valamint a releváns hirdetések megjelenítését.
            </p>
          </Section>

          <Section num="02" icon={Settings} title="Hogyan működik a cookie-k használata?">
            <p>
              A rendszer egy egyedi azonosítót hoz létre, amelyet a böngésző eltárol. Visszatéréskor a
              böngésző elküldi ezt az azonosítót – így működik például:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              {[
                'Bejelentkezési állapot megjegyzése',
                'Űrlapértékek tárolása',
                'Konverziók mérése',
                'Remarketing-hirdetések',
                'Személyre szabott tartalom',
                'Statisztikai adatgyűjtés',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 px-4 py-2.5 bg-[#faf7f1] rounded-xl border border-[#ece7df]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2BA8C0] shrink-0" />
                  <span className="text-[14px]">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section num="03" icon={Target} title="A weboldalon használt sütik céljai">
            <p>
              A weboldal sütiket használ a működéséhez, a felhasználói élmény javításához, statisztikai
              mérésekhez, teljesítményméréshez, hirdetésekhez, kampánykövetéshez és CRM-folyamatokhoz.
            </p>
          </Section>

          <Section num="04" icon={BarChart3} title="A sütik típusai">
            <div className="space-y-4">
              <div className="bg-[#faf7f1] rounded-2xl p-5 border border-[#ece7df]">
                <h3 className="font-serif-display text-[#1a1a1a] text-xl mb-2 flex items-center gap-2">
                  <span className="text-[#2BA8C0] text-sm font-sans">4.1</span> Alapműködést biztosító sütik
                </h3>
                <p className="text-[14.5px] text-[#3a3a3a]">
                  Session sütik, értesítési üzenetek állapota, bejelentkezési adatok, sütibeállítások,
                  anonimizált Analytics sütik.
                </p>
              </div>

              <div className="bg-[#faf7f1] rounded-2xl p-5 border border-[#ece7df]">
                <h3 className="font-serif-display text-[#1a1a1a] text-xl mb-2 flex items-center gap-2">
                  <span className="text-[#2BA8C0] text-sm font-sans">4.2</span> Statisztikai és teljesítménymérő sütik
                </h3>
                <p className="text-[14.5px] text-[#3a3a3a]">
                  Google Analytics, Google Optimize – a felhasználói viselkedést elemzik személyes
                  azonosítás nélkül.
                </p>
              </div>

              <div className="bg-[#faf7f1] rounded-2xl p-5 border border-[#ece7df]">
                <h3 className="font-serif-display text-[#1a1a1a] text-xl mb-3 flex items-center gap-2">
                  <span className="text-[#2BA8C0] text-sm font-sans">4.3</span> Célzó- és hirdetési sütik
                </h3>
                <p className="text-[14.5px] text-[#3a3a3a] mb-4">
                  Részletes tájékoztatóért kattintson az alábbi szolgáltatásokra:
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {adServices.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between px-4 py-2.5 bg-white rounded-xl border border-[#e8e4dc] hover:border-[#2BA8C0] hover:bg-[#2BA8C0]/5 transition-colors"
                    >
                      <span className="text-[14px] text-[#1a1a1a]">{s.name}</span>
                      <ExternalLink className="w-3.5 h-3.5 text-[#9a9a9a] group-hover:text-[#2BA8C0] transition-colors" />
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-[#faf7f1] rounded-2xl p-5 border border-[#ece7df]">
                <h3 className="font-serif-display text-[#1a1a1a] text-xl mb-2 flex items-center gap-2">
                  <span className="text-[#2BA8C0] text-sm font-sans">4.4</span> GoHighLevel sütik
                </h3>
                <p className="text-[14.5px] text-[#3a3a3a]">
                  Kampány- és funnelkövetés, űrlapadatok kezelése, automatizált e-mail / SMS indítás.
                </p>
              </div>
            </div>
          </Section>

          <Section num="05" icon={Settings} title="A sütik kezelése és kikapcsolása">
            <p>
              A felhasználó a böngészőjében bármikor módosíthatja vagy törölheti a sütiket. Felhívjuk
              figyelmét, hogy a tiltás csökkentheti a weboldal működésének minőségét.
            </p>
          </Section>

          <Section num="06" icon={Cookie} title="Böngésző-specifikus beállítások">
            <p>A legnépszerűbb böngészők süti beállításairól az alábbi linkeken tájékozódhat:</p>
            <div className="grid sm:grid-cols-2 gap-2 pt-2">
              {browsers.map((b) => (
                <a
                  key={b.name}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between px-4 py-3 bg-[#faf7f1] hover:bg-[#2BA8C0] hover:text-white rounded-xl border border-[#ece7df] transition-colors"
                >
                  <span className="text-[14px] font-medium">{b.name}</span>
                  <ExternalLink className="w-4 h-4 text-[#9a9a9a] group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </Section>

          <Section num="07" icon={Shield} title="Jogszabályi háttér">
            <ul className="grid sm:grid-cols-2 gap-2">
              {[
                '2003. évi C. törvény',
                '2001. évi CVIII. törvény',
                '2011. évi CXII. törvény',
                '2002/58/EK irányelv',
                'GDPR (EU 2016/679)',
              ].map((law) => (
                <li key={law} className="flex items-center gap-2.5 px-4 py-2.5 bg-[#faf7f1] rounded-xl border border-[#ece7df]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2BA8C0] shrink-0" />
                  <span className="text-[14px]">{law}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Contact CTA */}
          <div className="mt-14 pt-10 border-t border-[#e8e4dc] text-center">
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1a1a1a] mb-3">Kérdése van?</h2>
            <p className="text-[#3a3a3a] text-[15px] leading-relaxed mb-6 max-w-lg mx-auto">
              Süti szabályzatunkkal kapcsolatos kérdéseit írja meg az alábbi elérhetőségen.
            </p>
            <a
              href={`mailto:${TOP_BAR.email}`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#2BA8C0] text-white text-[14px] font-medium hover:bg-[#239ab1] transition-colors shadow-md"
            >
              {TOP_BAR.email}
            </a>
          </div>
        </article>

        <div className="text-center mt-10">
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0a1d2e] text-white text-[14px] font-medium hover:bg-[#2BA8C0] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>
        </div>
      </div>
    </div>
  );
}
